/**
 * ConfirmDialog.jsx — Premium Dialog & Toast System
 * Covers: delete confirmations, logout, save/update, success/error/warning/info alerts
 * Usage:
 *   import { useDialog, DialogProvider, Toast } from './ConfirmDialog';
 *   const { confirm, alert: dlgAlert } = useDialog();
 *   await confirm({ type:'danger', title:'...', message:'...', confirmText:'Siba' })
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// ─── CSS injected once ────────────────────────────────────────────────────────
const CSS = `
@keyframes dlg-backdrop-in  { from { opacity:0 } to { opacity:1 } }
@keyframes dlg-modal-in     { from { opacity:0; transform:translate(-50%,-50%) scale(.92) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }
@keyframes dlg-modal-out    { from { opacity:1; transform:translate(-50%,-50%) scale(1) } to { opacity:0; transform:translate(-50%,-50%) scale(.92) } }
@keyframes toast-in-right   { from { opacity:0; transform:translateX(110%) } to { opacity:1; transform:translateX(0) } }
@keyframes toast-out-right  { from { opacity:1; transform:translateX(0) } to { opacity:0; transform:translateX(110%) } }
@keyframes toast-progress   { from { width:100% } to { width:0% } }
@keyframes dlg-icon-pop     { 0%{transform:scale(0) rotate(-20deg)} 70%{transform:scale(1.15) rotate(4deg)} 100%{transform:scale(1) rotate(0)} }

.dlg-backdrop {
  position:fixed; inset:0; z-index:99990;
  background:rgba(0,0,0,.55);
  backdrop-filter:blur(4px);
  -webkit-backdrop-filter:blur(4px);
  animation:dlg-backdrop-in .2s ease;
  display:flex; align-items:center; justify-content:center;
}
.dlg-modal {
  position:fixed; z-index:99991;
  top:50%; left:50%;
  transform:translate(-50%,-50%);
  width:min(92vw,440px);
  background:var(--dlg-bg,#fff);
  border-radius:20px;
  box-shadow:0 24px 64px rgba(0,0,0,.18), 0 4px 16px rgba(0,0,0,.08);
  overflow:hidden;
  animation:dlg-modal-in .25s cubic-bezier(.34,1.56,.64,1);
}
.dlg-modal.closing { animation:dlg-modal-out .18s ease forwards; }
.dlg-icon-wrap {
  width:64px; height:64px; border-radius:50%;
  display:flex; align-items:center; justify-content:center;
  margin:0 auto 16px;
  animation:dlg-icon-pop .4s cubic-bezier(.34,1.56,.64,1) .1s both;
}
.dlg-body { padding:32px 28px 24px; text-align:center; }
.dlg-title { font-size:1.15rem; font-weight:700; margin:0 0 8px; line-height:1.3; }
.dlg-message { font-size:.9rem; line-height:1.6; margin:0; }
.dlg-footer { display:flex; gap:10px; padding:0 28px 24px; }
.dlg-footer.stacked { flex-direction:column; }
.dlg-btn {
  flex:1; min-height:44px; border:none; border-radius:12px;
  font-size:.9rem; font-weight:600; cursor:pointer;
  display:flex; align-items:center; justify-content:center; gap:6px;
  transition:all .2s ease; position:relative; overflow:hidden;
}
.dlg-btn:hover:not(:disabled) { transform:translateY(-1px); filter:brightness(1.06); }
.dlg-btn:active:not(:disabled) { transform:scale(.97); }
.dlg-btn:disabled { opacity:.55; cursor:not-allowed; }
.dlg-btn-spinner {
  width:16px; height:16px; border:2px solid rgba(255,255,255,.4);
  border-top-color:#fff; border-radius:50%;
  animation:dlg-spin .7s linear infinite;
}
@keyframes dlg-spin { to { transform:rotate(360deg) } }

/* Toast stack */
.dlg-toast-stack {
  position:fixed; top:20px; right:20px; z-index:99995;
  display:flex; flex-direction:column; gap:10px;
  pointer-events:none;
  max-width:min(92vw,380px);
}
.dlg-toast {
  pointer-events:auto;
  background:var(--dlg-bg,#fff);
  border-radius:14px;
  box-shadow:0 8px 32px rgba(0,0,0,.14), 0 2px 8px rgba(0,0,0,.06);
  overflow:hidden;
  animation:toast-in-right .3s cubic-bezier(.34,1.56,.64,1);
  border-left:4px solid transparent;
}
.dlg-toast.removing { animation:toast-out-right .25s ease forwards; }
.dlg-toast-inner { display:flex; align-items:flex-start; gap:12px; padding:14px 14px 10px; }
.dlg-toast-icon { font-size:1.25rem; flex-shrink:0; margin-top:1px; }
.dlg-toast-content { flex:1; min-width:0; }
.dlg-toast-title { font-size:.88rem; font-weight:700; margin:0 0 2px; }
.dlg-toast-msg { font-size:.82rem; line-height:1.5; margin:0; opacity:.8; }
.dlg-toast-close {
  background:none; border:none; cursor:pointer; padding:4px;
  opacity:.45; font-size:1rem; flex-shrink:0; line-height:1;
  transition:opacity .15s;
}
.dlg-toast-close:hover { opacity:.9; }
.dlg-toast-progress {
  height:3px; animation:toast-progress linear forwards;
}

/* Dark mode */
[data-theme="dark"] .dlg-modal,
[data-theme="dark"] .dlg-toast {
  --dlg-bg:#0f1f3d;
  color:#f1f5f9;
}
[data-theme="dark"] .dlg-message,
[data-theme="dark"] .dlg-toast-msg { color:#94a3b8; }

@media (max-width:480px) {
  .dlg-modal { width:min(96vw,400px); }
  .dlg-body { padding:24px 20px 18px; }
  .dlg-footer { padding:0 20px 20px; flex-direction:column; }
  .dlg-toast-stack { top:12px; right:12px; left:12px; max-width:100%; }
}
@media (prefers-reduced-motion:reduce) {
  .dlg-modal, .dlg-backdrop, .dlg-toast { animation:none !important; }
}
`;

// ─── Colour palettes per type ─────────────────────────────────────────────────
const PALETTE = {
  danger:  { icon:'🗑️', iconBg:'#fef2f2', iconColor:'#dc2626', confirmBg:'#dc2626', confirmHover:'#b91c1c', cancelBg:'#f1f5f9', cancelColor:'#374151', border:'#dc2626', toastBorder:'#ef4444', toastIcon:'❌' },
  warning: { icon:'⚠️', iconBg:'#fffbeb', iconColor:'#d97706', confirmBg:'#d97706', confirmHover:'#b45309', cancelBg:'#f1f5f9', cancelColor:'#374151', border:'#f59e0b', toastBorder:'#f59e0b', toastIcon:'⚠️' },
  success: { icon:'✅', iconBg:'#ecfdf5', iconColor:'#059669', confirmBg:'#059669', confirmHover:'#047857', cancelBg:'#f1f5f9', cancelColor:'#374151', border:'#10b981', toastBorder:'#10b981', toastIcon:'✅' },
  info:    { icon:'ℹ️', iconBg:'#eff6ff', iconColor:'#2563eb', confirmBg:'#2563eb', confirmHover:'#1d4ed8', cancelBg:'#f1f5f9', cancelColor:'#374151', border:'#3b82f6', toastBorder:'#3b82f6', toastIcon:'ℹ️' },
  logout:  { icon:'🚪', iconBg:'#fef2f2', iconColor:'#dc2626', confirmBg:'#dc2626', confirmHover:'#b91c1c', cancelBg:'#f1f5f9', cancelColor:'#374151', border:'#dc2626', toastBorder:'#ef4444', toastIcon:'👋' },
};

// ─── Context ──────────────────────────────────────────────────────────────────
const DialogCtx = createContext(null);
const ToastCtx  = createContext(null);

// ─── DialogProvider ───────────────────────────────────────────────────────────
export function DialogProvider({ children }) {
  const [dialog, setDialog]   = useState(null);
  const [toasts, setToasts]   = useState([]);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const resolveRef = useRef(null);

  // Inject CSS once
  useEffect(() => {
    if (document.getElementById('dlg-css')) return;
    const s = document.createElement('style');
    s.id = 'dlg-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }, []);

  const closeDialog = useCallback((result) => {
    setClosing(true);
    setTimeout(() => {
      setDialog(null);
      setClosing(false);
      setLoading(false);
      resolveRef.current?.(result);
      resolveRef.current = null;
    }, 180);
  }, []);

  // confirm({ type, title, message, confirmText, cancelText, onConfirm })
  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog(opts);
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (dialog?.onConfirm) {
      setLoading(true);
      try { await dialog.onConfirm(); } catch {}
      setLoading(false);
    }
    closeDialog(true);
  }, [dialog, closeDialog]);

  // toast({ type, title, message, duration })
  const toast = useCallback((opts) => {
    const id = Date.now() + Math.random();
    const duration = opts.duration ?? 4000;
    setToasts(prev => [...prev, { ...opts, id, duration }]);
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, removing: true } : t));
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 280);
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, removing: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 280);
  }, []);

  const p = dialog ? (PALETTE[dialog.type] || PALETTE.info) : null;

  return (
    <DialogCtx.Provider value={{ confirm }}>
      <ToastCtx.Provider value={{ toast }}>
        {children}

        {/* ── Modal ── */}
        {dialog && (
          <>
            <div className="dlg-backdrop" onClick={() => !loading && closeDialog(false)} />
            <div className={`dlg-modal${closing ? ' closing' : ''}`} role="dialog" aria-modal="true" aria-labelledby="dlg-title">
              <div className="dlg-body">
                <div className="dlg-icon-wrap" style={{ background: p.iconBg }}>
                  <span style={{ fontSize: '1.75rem' }}>{dialog.icon || p.icon}</span>
                </div>
                <h2 id="dlg-title" className="dlg-title" style={{ color: 'var(--dlg-title-color, #111827)' }}>
                  {dialog.title}
                </h2>
                {dialog.message && (
                  <p className="dlg-message" style={{ color: 'var(--dlg-msg-color, #6b7280)' }}>
                    {dialog.message}
                  </p>
                )}
              </div>
              <div className="dlg-footer">
                {dialog.cancelText !== false && (
                  <button
                    className="dlg-btn"
                    style={{ background: p.cancelBg, color: p.cancelColor }}
                    onClick={() => closeDialog(false)}
                    disabled={loading}
                  >
                    {dialog.cancelText || 'Bireke'}
                  </button>
                )}
                <button
                  className="dlg-btn"
                  style={{ background: p.confirmBg, color: '#fff', boxShadow: `0 4px 14px ${p.confirmBg}55` }}
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {loading
                    ? <span className="dlg-btn-spinner" />
                    : (dialog.confirmText || 'Emeza')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Toast stack ── */}
        <div className="dlg-toast-stack" aria-live="polite">
          {toasts.map(t => {
            const tp = PALETTE[t.type] || PALETTE.info;
            return (
              <div
                key={t.id}
                className={`dlg-toast${t.removing ? ' removing' : ''}`}
                style={{ borderLeftColor: tp.toastBorder }}
              >
                <div className="dlg-toast-inner">
                  <span className="dlg-toast-icon">{t.icon || tp.toastIcon}</span>
                  <div className="dlg-toast-content">
                    {t.title && <p className="dlg-toast-title" style={{ color: 'var(--dlg-title-color,#111827)' }}>{t.title}</p>}
                    {t.message && <p className="dlg-toast-msg">{t.message}</p>}
                  </div>
                  <button className="dlg-toast-close" onClick={() => removeToast(t.id)} aria-label="Funga">✕</button>
                </div>
                <div
                  className="dlg-toast-progress"
                  style={{ background: tp.toastBorder, animationDuration: `${t.duration}ms` }}
                />
              </div>
            );
          })}
        </div>
      </ToastCtx.Provider>
    </DialogCtx.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────
export function useDialog() {
  const ctx = useContext(DialogCtx);
  if (!ctx) throw new Error('useDialog must be used inside <DialogProvider>');
  return ctx;
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <DialogProvider>');
  return ctx;
}

// ─── Convenience presets ──────────────────────────────────────────────────────
export function useConfirmDelete() {
  const { confirm } = useDialog();
  return (opts = {}) => confirm({
    type: 'danger',
    title: opts.title || 'Gusiba?',
    message: opts.message || 'Urashaka koko gusiba iki? Iki gikorwa ntigishobora gusubizwa inyuma.',
    confirmText: opts.confirmText || '🗑️ Siba',
    cancelText: opts.cancelText || 'Bireke',
    ...opts,
  });
}

export function useConfirmLogout() {
  const { confirm } = useDialog();
  return (opts = {}) => confirm({
    type: 'logout',
    title: 'Ushaka koko gusohoka?',
    message: 'Uzasohoka muri konti yawe. Uzakenera kwinjira nanone.',
    confirmText: '🚪 Ego, sohoka',
    cancelText: 'Oya, garuka',
    ...opts,
  });
}

export function useConfirmSave() {
  const { confirm } = useDialog();
  return (opts = {}) => confirm({
    type: 'info',
    title: 'Kubika impinduka?',
    message: 'Urashaka koko kubika ibi bikorwa?',
    confirmText: '💾 Bika',
    cancelText: 'Bireke',
    ...opts,
  });
}
